"use client";

import React, { useEffect, useState } from "react";
import AnalyticsSidebar from "../../AnalyticsSidebar";
import { Navbar } from "../../../../components/navbar";
import { Footer } from "../../../../components/footer";
import FormInput from "../../../../components/FormInput";
import styles from "../css/ExpiredStock.module.css";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { getExpiredStock } from "../../../../api/analytics/analyticsApi.js";

export default function ExpiredStockAnalytics() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const from = fromDate && toDate ? new Date(fromDate) : undefined;
        const to = fromDate && toDate ? new Date(toDate) : undefined;

        const rows = await getExpiredStock({
          fromDate: from,
          toDate: to,
        });
        if (!isMounted) return;

        const chartData = (rows || []).map((row) => ({
          name: row.categoryName,
          value: Number(row.expiredQuantity ?? 0),
        }));

        setData(chartData);
      } catch (err) {
        if (!isMounted) return;
        setError(err?.message || "Failed to load expired stock");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    if ((fromDate && toDate) || (!fromDate && !toDate)) {
      load();
    }

    return () => {
      isMounted = false;
    };
  }, [fromDate, toDate]);

  const chartWidth = Math.max((data?.length || 0) * 120, 600);

  return (
    <div className={styles.app}>
      <div className={styles.mainContainer}>
        <Navbar backTo="/analytics" />

        <div className={`${styles.contentArea} analytics-content`}>
          <AnalyticsSidebar />
          <div className={styles.contentAreaFull}>
            <section className={styles.panelArea}>
              <h2 className={styles.pageTitle}>Expired Stock</h2>

              <div className={styles.filtersRow}>
                <div className={styles.inlineFilter}>
                  <span>From</span>
                  <FormInput
                    type="date"
                    name="fromDate"
                    value={fromDate || ""}
                    onChange={(e) => {
                      const value = e.target.value || "";
                      setFromDate(value);
                    }}
                  />
                </div>
                <div className={styles.inlineFilter}>
                  <span>To</span>
                  <FormInput
                    type="date"
                    name="toDate"
                    value={toDate || ""}
                    onChange={(e) => {
                      const value = e.target.value || "";
                      setToDate(value);
                    }}
                  />
                </div>
              </div>

              <div className={styles.chartCard}>
                {error && (
                  <div className="text-red-500 text-sm mb-2">{error}</div>
                )}
                {!loading && !error && (!data || data.length === 0) ? (
                  <p className="text-gray-500 text-sm text-center py-8">
                    No data available at the moment
                  </p>
                ) : (
                  <div className={styles.chartScroll}>
                    <div className={styles.chartInner} style={{ width: chartWidth }}>
                      <BarChart
                        width={chartWidth}
                        height={360}
                        data={data}
                        margin={{ top: 28, right: 24, left: 24, bottom: 16 }}
                        barCategoryGap={14}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="name"
                          tick={{ fontSize: 12 }}
                          interval={0}
                          angle={-30}
                          textAnchor="end"
                        />
                        <YAxis />
                        <Tooltip />
                        <Legend
                          verticalAlign="top"
                          align="center"
                          formatter={() => "Expired Stock"}
                        />
                        <Bar
                          dataKey="value"
                          fill="#A78BFA"
                          radius={[4, 4, 0, 0]}
                          barSize={60}
                        />
                      </BarChart>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>

        <div className={styles.footerWrapper}>
          <Footer />
        </div>
      </div>
    </div>
  );
}
